// src/components/Onboarding.js
import React, { useState } from "react";
import { api } from "../api";
import "./Onboarding.css";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const GOALS = [
  { label: "Gain muscle", value: "muscle" },
  { label: "Weight loss", value: "weight_loss" },
  { label: "Build stamina", value: "stamina" },
];
const TRAINING = ["gym", "home", "both"];

const onboardingBg = "/images/olympeak_visual_4.png";

function ymdToday() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(
    t.getDate()
  ).padStart(2, "0")}`;
}

export default function Onboarding({ onComplete, onSkip, planOnly = false }) {
  const [step, setStep] = useState(0); // 0–2 = plan steps, 3 = skip-confirm
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    goal: "",
    trainingPlace: "",
    trainingDays: [],
    planStart: ymdToday(),
    planDuration: 1,
  });

  const toggleDay = (day) => {
    setForm((f) => ({
      ...f,
      trainingDays: f.trainingDays.includes(day)
        ? f.trainingDays.filter((d) => d !== day)
        : [...f.trainingDays, day],
    }));
  };

  const goalValid = !!form.goal;
  const trainingValid = form.trainingPlace && form.trainingDays.length >= 2;

  const handleSubmit = async () => {
    try {
      await api(`/me/plan/generate`, {
        method: "POST",
        body: form,
      });
      onComplete?.(form);
    } catch (err) {
      console.error("❌ Plan generation failed", err);
      setError("Something went wrong while generating your plan.");
    }
  };

  function getStepConfig(step) {
    switch (step) {
      case 0:
        return {
          title: "Your Goal",
          subtitle: "",
          body: (
            <div className="pill-row">
              {GOALS.map((g) => (
                <div
                  key={g.value}
                  className={`pill ${form.goal === g.value ? "active" : ""}`}
                  onClick={() => {
                    setError("");
                    setForm({ ...form, goal: g.value });
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {g.label}
                </div>
              ))}
            </div>
          ),
        };

      case 1:
        return {
          title: "Training Setup",
          subtitle: "",
          body: (
            <>
              <p className="onboarding-section-label">Where do you want to train?</p>
              <div className="pill-row">
                {TRAINING.map((t) => (
                  <div
                    key={t}
                    className={`pill ${form.trainingPlace === t ? "active" : ""}`}
                    onClick={() => {
                      setError("");
                      setForm({ ...form, trainingPlace: t });
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {t}
                  </div>
                ))}
              </div>

              <p className="onboarding-section-label">Which days?</p>
              <div className="pill-row">
                {DAYS.map((d) => {
                  const selected = form.trainingDays.includes(d);
                  return (
                    <label key={d} className={`pill day ${selected ? "active" : ""}`}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => {
                          setError("");
                          toggleDay(d);
                        }}
                      />
                      {d}
                    </label>
                  );
                })}
              </div>
            </>
          ),
        };

      case 2:
        return {
          title: "Plan Duration",
          subtitle: "",
          body: (
            <>
              <p className="onboarding-section-label">When do you want to start?</p>
              <input
                type="date"
                value={form.planStart}
                onChange={(e) => setForm({ ...form, planStart: e.target.value })}
              />

              <p className="onboarding-section-label">For how long?</p>
              <select
                value={form.planDuration}
                onChange={(e) =>
                  setForm({ ...form, planDuration: Number(e.target.value) })
                }
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {m} {m === 1 ? "month" : "months"}
                  </option>
                ))}
              </select>
            </>
          ),
        };

      case 3:
        return {
          title: "Skip plan creation?",
          subtitle: "",
          body: (
            <>
              <p className="onboarding-section-label">
                Would you like to skip the plan creation for now?
              </p>
              <p className="onboarding-section-label">
                You can always create a plan later from the <strong>Plan</strong> tab.
              </p>
            </>
          ),
        };

      default:
        return { title: "", subtitle: "", body: null };
    }
  }

  const totalSteps = 3;
  const logicalStepIndex = Math.min(step, totalSteps - 1);
  const progressPct = ((logicalStepIndex + 1) / totalSteps) * 100;

  const isStepValid =
    (step === 0 && goalValid) ||
    (step === 1 && trainingValid) ||
    step === 2;

  const currentStep = getStepConfig(step);

  const goToSkipConfirm = () => {
    setError("");
    setStep(3);
  };

  return (
    <div
      className="page-wrapper onboarding-bg"
      style={{ backgroundImage: `url(${onboardingBg})` }}
    >
      <div className="page-container onboarding-container">
        <div className="app-card onboarding glass">
          <div className="progress">
            <span style={{ "--pct": `${progressPct}%` }} />
          </div>

          <div className="onboarding-header">
            <h2>{currentStep.title}</h2>
            {currentStep.subtitle && (
              <p className="onboarding-subtitle">{currentStep.subtitle}</p>
            )}
          </div>

          <div className="onboarding-body">{currentStep.body}</div>

          <div className={`onboarding-error-row ${error && step !== 3 ? "show" : ""}`}>
            <div className="onboarding-error-text">
              {error && step !== 3 ? error : "\u00A0"}
            </div>
          </div>

          <div className="nav">
            <div className="nav-left">
              {/* In planOnly mode (PlanPage), still allow skipping, but it only closes onboarding there */}
              {step === 0 && (
                <button className="btn" onClick={goToSkipConfirm}>
                  Skip plan creation
                </button>
              )}

              {step > 0 && step < 3 && (
                <>
                  <button className="btn" onClick={() => setStep((s) => s - 1)}>
                    Back
                  </button>
                  <button className="btn" onClick={goToSkipConfirm}>
                    Skip plan creation
                  </button>
                </>
              )}

              {step === 3 && (
                <button className="btn" onClick={() => setStep(0)}>
                  Continue creation
                </button>
              )}
            </div>

            <div className="nav-right">
              {step < 2 && (
                <button
                  className="btn primary"
                  onClick={() => {
                    if (!isStepValid) {
                      if (step === 0) setError("Choose at least one goal");
                      else if (step === 1)
                        setError("Select training place and at least 2 days");
                      return;
                    }
                    setError("");
                    setStep((s) => s + 1);
                  }}
                >
                  Next
                </button>
              )}

              {step === 2 && (
                <button className="btn primary" onClick={handleSubmit}>
                  Finish
                </button>
              )}

              {step === 3 && (
                <button
                  className="btn primary"
                  onClick={() => {
                    // ✅ The whole point: do NOT reload, just inform AppGate (or PlanPage)
                    if (typeof onSkip === "function") {
                      onSkip();
                      return;
                    }

                    // fallback: if no onSkip provided, at least exit quietly
                    // (PlanPage should pass onSkip to close the modal if it uses it)
                    if (planOnly && typeof onComplete === "function") {
                      onComplete(null);
                    }
                  }}
                >
                  Skip for now
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}